import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../Layout/Layout";
import { isAuthenticated } from "../../auth";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import DropshippingInterneLayout from "../components/DropshippingInterneLayout";
import { getOwnStorefront, publishStorefront, saveStorefrontDraft } from "../services/storefrontService";
import { PROVIDER_ROUTES } from "../providerWorkspace";
import ProviderStorefrontView from "../storefront/ProviderStorefrontView";
import useProviderStorefront from "../storefront/useProviderStorefront";
import { buildProviderStoreConfig, PROVIDER_ELEMENT_PRESETS } from "../storefront/providerStoreConfig";
import { pagesOf } from "../storefront/storefrontPages";
import { StorefrontBadge } from "./ProviderCenter";
import ProviderNotice from "./ProviderNotice";
import { buildStorefrontDatasets } from "./storefrontData";
import "../seller/DropshippingIn.css";
import "./center.css";
import "./providerCenter.css";
import ShopBar from "../../shops/ShopBar";
import { useActiveShopChange } from "../../shops/accountShopsStore";

const PageBuilder = React.lazy(() => import("../../pageInside/PageBuilder/PageBuilder"));

const ok = (r) => r && !r.error && r.success !== false;

// /provider/my-shop/builder[?page=<pageId>] -- the existing Elakay Page Builder (same editor as Seller
// shops: templates, Pages, Layers, HTML, Shop elements...) editing the Provider storefront document.
// `?page=` opens that page. Saving goes through the Provider storefront API (never Seller My Shop);
// the builder data are the Provider's own products, categories and real statistics; its Preview and
// the "Preview as Sellers see it" view below render through the SAME storefront renderer as the
// public storefront.
export default function ProviderStorefrontBuilder({ infoSource }) {
  const token = isAuthenticated()?.token;
  const [params] = useSearchParams();
  const requestedPage = params.get("page");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [storefront, setStorefront] = useState(null);
  const [page, setPage] = useState(null);
  const [profile, setProfile] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [currentPageId, setCurrentPageId] = useState(requestedPage);
  const builderDocRef = useRef(null);
  const live = useProviderStorefront(profile?.provider?._id || null);
  const reloadLive = live.reload;

  const load = useCallback(async () => {
    setLoading(true);
    const own = await getOwnStorefront(token);
    if (!ok(own)) { setError(own?.error || text("h_7f2f6a15cf8da2", "Error")); setLoading(false); return; }
    setError("");
    setStorefront(own.storefront);
    setPage(own.page);
    setProfile(own.profile || null);
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useActiveShopChange("provider", useCallback(() => {
    setMsg(null);
    load();
    reloadLive();
  }, [load, reloadLive]));

  const summary = live.storefront || profile;
  const datasets = useMemo(() => buildStorefrontDatasets(summary, live.products, live.categories), [summary, live.products, live.categories]);
  const storeConfig = useMemo(() => buildProviderStoreConfig({ mode: "owner" }), []);
  // The builder opens on the requested page when it exists in the saved draft.
  const initialDoc = useMemo(() => {
    if (!page?.doc) return null;
    const ids = pagesOf(page.doc).map((p) => String(p.id));
    return requestedPage && ids.includes(String(requestedPage)) ? { ...page.doc, currentPageId: requestedPage } : page.doc;
  }, [page, requestedPage]);

  const handleSave = async (payload) => {
    const r = await saveStorefrontDraft(token, { doc: payload?.doc, title: payload?.title, description: payload?.description });
    if (!ok(r)) throw new Error(r?.error || "Error");
    setStorefront(r.storefront);
    setPage(r.page);
    setMsg({ ok: true, text: text("h_pvc_saved_draft", "Draft saved. Sellers still see your published pages until you publish.") });
    return r.page;
  };

  const savedRow = (storefront?.pages || []).find((r) => String(r.id) === String(currentPageId || pagesOf(page?.doc)[0]?.id) && r.inDraft);
  const publish = async (pageIds) => {
    setPublishing(true); setMsg(null);
    const r = await publishStorefront(token, pageIds);
    setPublishing(false);
    if (!ok(r)) { setMsg({ ok: false, text: r.error }); return; }
    setStorefront(r.storefront);
    setMsg({ ok: true, text: pageIds ? text("h_pvc_page_published", "“{value0}” is published.", { value0: savedRow?.name || "" }) : text("h_pvc_published_msg", "Your storefront is published. Sellers now see it on your Provider shop.") });
    live.reload();
  };

  // "Preview as Sellers see it": the CURRENT builder document (unsaved edits included) through the
  // storefront renderer, with the saved page settings (Home, menu).
  const previewCustom = () => {
    const doc = builderDocRef.current || page?.doc;
    if (!doc) return null;
    const rows = storefront?.pages || [];
    return {
      doc,
      htmlSource: page?.htmlSource,
      htmlSourcePages: page?.htmlSourcePages,
      homePageId: storefront?.homePageId,
      pages: pagesOf(doc).map((p, i) => rows.find((r) => String(r.id) === String(p.id)) || { id: p.id, name: p.name, showInNavigation: true, navigationOrder: 100 + i }),
    };
  };

  return (
    <Layout infoSource={infoSource} titleElakay="providerProducts" SliderPunShow={false} loading={false} outOffPage={false} {...infoSource?.allSettings?.layout} showFooter={true}>
      <DropshippingInterneLayout infoSource={infoSource}>
        <div className="dsi-page ctr pvc-builder">
          <nav className="dsi-breadcrumb" aria-label={text("h_c766e665183df1", "Breadcrumb")}>
            <Link to={PROVIDER_ROUTES.dashboard}>{text("h_pvc_title", "Provider Center")}</Link><span aria-hidden="true">›</span>
            <Link to={PROVIDER_ROUTES.myShop}>{text("h_pvc_nav_myshop", "My Provider Shop")}</Link><span aria-hidden="true">›</span>
            <span aria-current="page">{text("h_pvc_nav_builder", "Provider Page Builder")}</span>
          </nav>
          <ProviderNotice />
          <ShopBar type="provider" />
          {loading && <div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>}
          {!loading && error && <div className="dsi-state-card" role="alert"><h2>{text("h_spm_error_title", "Something went wrong")}</h2><p>{error}</p><button type="button" className="dsi-state-card__btn" onClick={load}>{text("h_spm_retry", "Retry")}</button></div>}
          {!loading && !error && <>
            <div className="pvc-builderbar">
              <span className="pvc-builderbar__title">
                <i className="fas fa-paint-brush" aria-hidden="true" />
                {profile?.shop?.shopName || text("h_pvc_nav_builder", "Provider Page Builder")}
                <StorefrontBadge storefront={storefront} />
                {storefront?.hasUnpublishedChanges && <span className="pvc-badge pvc-badge--changes">{text("h_pvc_unpublished_changes", "Unpublished changes")}</span>}
              </span>
              <Link to={PROVIDER_ROUTES.myShop} className="ctr__btn"><i className="fas fa-arrow-left" aria-hidden="true" />{text("h_pvc_back_myshop", "Back to My Shop")}</Link>
              <button type="button" className="ctr__btn" aria-pressed={preview} onClick={() => setPreview((v) => !v)}>
                <i className={`fas ${preview ? "fa-pen" : "fa-eye"}`} aria-hidden="true" />{preview ? text("h_pvc_back_builder", "Back to Builder") : text("h_pvc_preview_as_sellers", "Preview as Sellers see it")}
              </button>
              <button type="button" className="ctr__btn" onClick={() => publish([savedRow.id])} disabled={!savedRow || publishing || (savedRow.published && !savedRow.hasUnpublishedChanges)}
                title={!savedRow ? text("h_pvc_save_first", "Save this page first") : undefined}>
                <i className="fas fa-upload" aria-hidden="true" />{text("h_pvc_publish_this", "Publish this page")}
              </button>
              <button type="button" className="ctr__btn ctr__btn--primary" onClick={() => publish()} disabled={!storefront?.hasDraft || publishing || (storefront.isPublished && !storefront.hasUnpublishedChanges)}>
                <i className={`fas ${publishing ? "fa-spinner fa-spin" : "fa-upload"}`} aria-hidden="true" />{storefront?.isPublished ? text("h_pvc_publish_all", "Publish all changes") : text("h_pvc_publish_all_pages", "Publish all pages")}
              </button>
            </div>
            {msg && <div className={`ctr__msg${msg.ok ? "" : " ctr__msg--err"}`} role="status">{msg.text}</div>}
            <p className="pvc-builder__hint">{text("h_pvc_builder_hint2", "Save keeps a draft; Publish uses your last saved version. Add your real products, wholesale / dropshipping sections, categories and statistics from the 🏭 Provider group; they always show your live data.")}</p>
            {preview && (
              <div className="pvc-builder__canvas">
                <ProviderStorefrontView summary={summary} custom={previewCustom()} products={live.products} categories={live.categories}
                  pageId={currentPageId} onSelectPage={setCurrentPageId} mode="owner" infoSource={infoSource} token={token} productsLoading={live.loading} />
              </div>
            )}
            {/* The builder binds its live dataset once: it is mounted after the Provider data loaded. */}
            {profile?.provider?._id && !live.storefront && !live.error
              ? <div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>
              : <div className="pvc-builder__canvas" hidden={preview}>
              <React.Suspense fallback={<div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>}><PageBuilder
                key={page?._id ? "storefront" : "new-storefront"}
                initialDoc={initialDoc}
                pageMode="store"
                lockPageMode={true}
                liveDatasets={datasets}
                rendererStoreConfig={storeConfig}
                elementPresets={PROVIDER_ELEMENT_PRESETS}
                onDocChange={(doc) => { builderDocRef.current = doc; if (doc?.currentPageId && doc.currentPageId !== currentPageId) setCurrentPageId(doc.currentPageId); }}
                onSave={handleSave}
                savedPageId={page?._id || null}
                savedPage={page}
                currentUserId={isAuthenticated()?.user?._id || null}
                infoSource={infoSource}
                fitParentHeight={false}
                onPageMetaUpdated={(updated) => setPage((prev) => (prev && updated ? { ...prev, ...updated } : prev))}
              /></React.Suspense>
            </div>}
          </>}
        </div>
      </DropshippingInterneLayout>
    </Layout>
  );
}
